/**
 * Dwell Detection Utility
 * Identifies dwell locations (stops) from GPS trajectory data
 */

import { PathfinderDataPoint } from '../services/pathfinderApi';
import { batchReverseGeocode } from './geocoding';

export interface DwellLocation {
  id: string | number;
  entity_id: string;
  device_type: string;
  latitude: number;
  longitude: number;
  start_time: string; // ISO string
  end_time: string; // ISO string
  duration_seconds: number;
  point_count?: number; // Optional since backend data might not have it
  site_id?: string; // ID for repeated visits grouping
  site_name?: string; // e.g., "Site A"
}

export interface DwellSite {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  visit_count: number;
  total_duration: number;
  dwells: DwellLocation[];
  first_visit: string;
  last_visit: string;
  unique_days: number;
  is_revisit: boolean;
  type: 'dwell' | 'pass-through';
  device_ids: string[]; // List of unique device IDs that visited this site
  unique_device_count: number; // Count of unique devices
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in meters
 */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Detects dwell locations from GPS data points
 * @param dataPoints List of GPS points
 * @param distanceThresholdMeters Max distance a point can be from dwell center to be included (default 100m)
 * @param timeThresholdSeconds Min duration to qualify as a dwell (default 300s / 5m)
 */
export function detectDwells(
  dataPoints: PathfinderDataPoint[],
  distanceThresholdMeters: number = 100,
  timeThresholdSeconds: number = 300
): DwellLocation[] {
  if (!dataPoints || dataPoints.length < 2) return [];

  // 1. Sort points by time
  const sortedPoints = [...dataPoints].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const dwells: DwellLocation[] = [];

  let currentDwellPoints: PathfinderDataPoint[] = [sortedPoints[0]];
  let anchorPoint = sortedPoints[0];

  for (let i = 1; i < sortedPoints.length; i++) {
    const point = sortedPoints[i];
    // Coordinates are internal format: [lat, lng], haversineDistance expects (lat, lon)
    const distance = haversineDistance(
      anchorPoint.coordinates[0],  // lat
      anchorPoint.coordinates[1],  // lon
      point.coordinates[0],        // lat
      point.coordinates[1]         // lon
    );

    if (distance <= distanceThresholdMeters) {
      // Point is within range, add to current dwell candidate
      currentDwellPoints.push(point);
    } else {
      // Point is outside range, check if current candidate qualifies as a dwell
      const startTime = currentDwellPoints[0].timestamp;
      const endTime = currentDwellPoints[currentDwellPoints.length - 1].timestamp;
      const duration = (endTime.getTime() - startTime.getTime()) / 1000;

      if (duration >= timeThresholdSeconds) {
        // It's a valid dwell!
        
        // Calculate centroid (coordinates are internal format: [lat, lng])
        const avgLat = currentDwellPoints.reduce((sum, p) => sum + p.coordinates[0], 0) / currentDwellPoints.length;
        const avgLon = currentDwellPoints.reduce((sum, p) => sum + p.coordinates[1], 0) / currentDwellPoints.length;

        const dwell: DwellLocation = {
          id: `generated-dwell-${startTime.getTime()}`,
          entity_id: currentDwellPoints[0].studentId || currentDwellPoints[0].deviceId,
          device_type: currentDwellPoints[0].deviceType || 'unknown',
          latitude: avgLat,
          longitude: avgLon,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          duration_seconds: duration,
          point_count: currentDwellPoints.length
        };
        dwells.push(dwell);
      }

      // Start new candidate
      currentDwellPoints = [point];
      anchorPoint = point;
    }
  }

  // Check the last candidate
  if (currentDwellPoints.length > 0) {
    const startTime = currentDwellPoints[0].timestamp;
    const endTime = currentDwellPoints[currentDwellPoints.length - 1].timestamp;
    const duration = (endTime.getTime() - startTime.getTime()) / 1000;

    if (duration >= timeThresholdSeconds) {
      // Calculate centroid (coordinates are internal format: [lat, lng])
      const avgLat = currentDwellPoints.reduce((sum, p) => sum + p.coordinates[0], 0) / currentDwellPoints.length;
      const avgLon = currentDwellPoints.reduce((sum, p) => sum + p.coordinates[1], 0) / currentDwellPoints.length;

      const dwell: DwellLocation = {
        id: `generated-dwell-${startTime.getTime()}`,
        entity_id: currentDwellPoints[0].studentId || currentDwellPoints[0].deviceId,
        device_type: currentDwellPoints[0].deviceType || 'unknown',
        latitude: avgLat,
        longitude: avgLon,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        duration_seconds: duration,
        point_count: currentDwellPoints.length
      };
      dwells.push(dwell);
    }
  }

  return dwells;
}

/**
 * Identifies repeated visits to the same sites (both dwells and pass-throughs)
 * @param dwells List of detected dwells
 * @param siteRadiusMeters Radius to consider grouping dwells into a site (default 150m)
 * @param allPoints Optional list of all raw GPS points to detect non-dwelling revisits
 */
export function identifySites(
  dwells: DwellLocation[],
  siteRadiusMeters: number = 150,
  allPoints: PathfinderDataPoint[] = []
): { dwells: DwellLocation[], sites: DwellSite[] } {
  const sites: DwellSite[] = [];
  const processedDwells = [...dwells]; // Clone to modify

  // 1. Cluster Dwells first (create Dwell Sites)
  processedDwells.forEach(dwell => {
    let assignedSite = sites.find(site => {
      const distance = haversineDistance(site.latitude, site.longitude, dwell.latitude, dwell.longitude);
      return distance <= siteRadiusMeters;
    });

    if (assignedSite) {
      assignedSite.visit_count++;
      assignedSite.total_duration += dwell.duration_seconds;
      assignedSite.dwells.push(dwell);
      
      // Track unique device IDs
      if (dwell.entity_id && !assignedSite.device_ids.includes(dwell.entity_id)) {
        assignedSite.device_ids.push(dwell.entity_id);
        assignedSite.unique_device_count = assignedSite.device_ids.length;
      }
      
      if (new Date(dwell.start_time) < new Date(assignedSite.first_visit)) assignedSite.first_visit = dwell.start_time;
      if (new Date(dwell.end_time) > new Date(assignedSite.last_visit)) assignedSite.last_visit = dwell.end_time;
      
      dwell.site_id = assignedSite.id;
      dwell.site_name = assignedSite.name;
    } else {
      const siteId = `site-${sites.length + 1}`;
      const siteName = `Site ${String.fromCharCode(65 + (sites.length % 26))}${Math.floor(sites.length / 26) || ''}`;
      
      const newSite: DwellSite = {
        id: siteId,
        name: siteName,
        latitude: dwell.latitude,
        longitude: dwell.longitude,
        visit_count: 1,
        total_duration: dwell.duration_seconds,
        dwells: [dwell],
        first_visit: dwell.start_time,
        last_visit: dwell.end_time,
        unique_days: 1,
        is_revisit: false,
        type: 'dwell',
        device_ids: dwell.entity_id ? [dwell.entity_id] : [],
        unique_device_count: dwell.entity_id ? 1 : 0
      };
      sites.push(newSite);
      dwell.site_id = siteId;
      dwell.site_name = siteName;
    }
  });

  // 2. Detect Pass-Through Sites (locations with >1 visit day but NO dwells)
  if (allPoints.length > 0) {
    // Grid-based detection for efficiency
    const gridSize = 100; // meters
    const latStep = gridSize / 111320;
    const lonStep = gridSize / 111320; // Approx
    
    const grid = new Map<string, { points: PathfinderDataPoint[], days: Set<string>, lat: number, lon: number }>();
    
    // Coordinates are in internal format: [lat, lng]
    allPoints.forEach(p => {
      const lat = p.coordinates[0];
      const lng = p.coordinates[1];
      const latIdx = Math.floor(lat / latStep);
      const lonIdx = Math.floor(lng / lonStep);
      const key = `${latIdx},${lonIdx}`;
      
      if (!grid.has(key)) {
        grid.set(key, { points: [], days: new Set(), lat: 0, lon: 0 });
      }
      const cell = grid.get(key)!;
      cell.points.push(p);
      cell.days.add(p.timestamp.toISOString().split('T')[0]);
    });

    // Find grids with > 1 unique day
    const activeGrids = Array.from(grid.values()).filter(c => c.days.size > 1);
    
    // Compute centroids (coordinates are internal format: [lat, lng])
    activeGrids.forEach(c => {
      c.lat = c.points.reduce((sum, p) => sum + p.coordinates[0], 0) / c.points.length;
      c.lon = c.points.reduce((sum, p) => sum + p.coordinates[1], 0) / c.points.length;
    });

    // Cluster grids into sites
    activeGrids.forEach(cell => {
      // Check if near ANY existing site (Dwell Site or Pass-Through Site)
      let assignedSite = sites.find(site => {
        const distance = haversineDistance(site.latitude, site.longitude, cell.lat, cell.lon);
        return distance <= siteRadiusMeters;
      });

      if (!assignedSite) {
        // New Pass-Through Site
        const siteId = `pt-site-${sites.length + 1}`;
        const siteName = `Loc ${sites.length + 1} (Pass-Through)`;
        
        // Sort points for time range
        const sorted = cell.points.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        
        // Collect unique device IDs from the points
        const deviceIds = Array.from(new Set(
          cell.points
            .map(p => p.studentId || p.deviceId)
            .filter((id): id is string => !!id)
        ));

        sites.push({
          id: siteId,
          name: siteName,
          latitude: cell.lat,
          longitude: cell.lon,
          visit_count: cell.points.length, // Count points as proxy for activity
          total_duration: 0,
          dwells: [],
          first_visit: sorted[0].timestamp.toISOString(),
          last_visit: sorted[sorted.length - 1].timestamp.toISOString(),
          unique_days: cell.days.size,
          is_revisit: true, // By definition if >1 day
          type: 'pass-through',
          device_ids: deviceIds,
          unique_device_count: deviceIds.length
        });
      }
    });
  }

  // 3. Update stats for ALL sites (Dwell and Pass-Through) using raw points
  if (allPoints.length > 0) {
    sites.forEach(site => {
      // Re-calculate unique days using ALL points near the site
      const siteDays = new Set<string>();
      // Add dwell days first
      site.dwells.forEach(d => siteDays.add(new Date(d.start_time).toISOString().split('T')[0]));
      
      // Add pass-through days (coordinates are internal format: [lat, lng])
      allPoints.forEach(point => {
        const pointDay = point.timestamp.toISOString().split('T')[0];
        if (!siteDays.has(pointDay)) {
          const distance = haversineDistance(site.latitude, site.longitude, point.coordinates[0], point.coordinates[1]);
          if (distance <= siteRadiusMeters) {
            siteDays.add(pointDay);
            // Update range
            if (point.timestamp > new Date(site.last_visit)) site.last_visit = point.timestamp.toISOString();
            if (point.timestamp < new Date(site.first_visit)) site.first_visit = point.timestamp.toISOString();
          }
        }
      });

      site.unique_days = siteDays.size;
      site.is_revisit = site.unique_days > 1;
    });
  } else {
    // Fallback if no raw points provided (only dwells)
    sites.forEach(site => {
      const days = new Set(site.dwells.map(d => new Date(d.start_time).toISOString().split('T')[0]));
      site.unique_days = days.size;
      site.is_revisit = site.unique_days > 1;
    });
  }

  return { dwells: processedDwells, sites };
}

/**
 * Enrich dwell sites with actual place names from Mapbox geocoding
 * If multiple sites have the same name, coordinates are appended for uniqueness
 * @param sites Array of DwellSite objects
 * @param onProgress Optional progress callback
 * @returns Updated sites with geocoded place names
 */
export async function enrichSitesWithPlaceNames(
  sites: DwellSite[],
  onProgress?: (completed: number, total: number) => void
): Promise<DwellSite[]> {
  if (sites.length === 0) return sites;

  // Prepare coordinates for batch geocoding
  const coordsToGeocode = sites.map(site => ({
    id: site.id,
    lat: site.latitude,
    lng: site.longitude
  }));

  // Batch geocode all sites
  const placeNames = await batchReverseGeocode(coordsToGeocode, onProgress);

  // Count name occurrences to detect duplicates
  const nameCounts = new Map<string, number>();
  placeNames.forEach((name) => {
    if (name) {
      nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
    }
  });

  // Helper to get unique name (add coords if duplicate)
  const getUniqueName = (name: string, lat: number, lng: number): string => {
    const count = nameCounts.get(name) || 0;
    if (count > 1) {
      // Multiple sites with same name - add coordinates
      return `${name} (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
    }
    return name;
  };

  // Update site names with geocoded results
  return sites.map(site => {
    const placeName = placeNames.get(site.id);
    if (placeName) {
      const uniqueName = getUniqueName(placeName, site.latitude, site.longitude);
      return {
        ...site,
        name: uniqueName,
        // Also update dwells that belong to this site
        dwells: site.dwells.map(dwell => ({
          ...dwell,
          site_name: uniqueName
        }))
      };
    }
    return site;
  });
}

/**
 * Enrich dwells array with geocoded site names
 * @param dwells Array of DwellLocation objects  
 * @param sites Array of DwellSite objects (already enriched with place names)
 * @returns Updated dwells with geocoded site names
 */
export function updateDwellsWithSiteNames(
  dwells: DwellLocation[],
  sites: DwellSite[]
): DwellLocation[] {
  const siteNameMap = new Map(sites.map(s => [s.id, s.name]));
  
  return dwells.map(dwell => {
    if (dwell.site_id && siteNameMap.has(dwell.site_id)) {
      return {
        ...dwell,
        site_name: siteNameMap.get(dwell.site_id)
      };
    }
    return dwell;
  });
}
